import { useEffect, useRef } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/lib/supabase";
import { ChannelNamingUtil, ChatNotificationToast } from "@semo/shared-chat";
import { 
  getChatControllerGetRoomsQueryKey,
  BaseChatMessageResponseDtoMessageType,
  BaseChatMessageResponseDtoSenderType,
  getNotificationsControllerGetUnreadCountQueryKey,
} from "@semo/api-contracts";
import { toast } from "sonner";

/**
 * Global Realtime hook for Vendors (Suppliers)
 *
 * Subscribes to the supplier's notification channel to receive:
 * 1. New message notifications (to update unread count and latest message in list)
 * 2. Read receipts (to update unread count in list)
 *
 * This hook should be used in a global layout or the root component.
 */
export function useGlobalChatRealtime(currentUserId?: string) {
  const queryClient = useQueryClient();
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (!currentUserId || !supabase) return;

    const debouncedInvalidate = () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
      timerRef.current = setTimeout(() => {
        // ✅ Invalidate Badge Count
        queryClient.invalidateQueries({
          queryKey: getNotificationsControllerGetUnreadCountQueryKey(),
        });

        // ✅ Invalidate Chat Room List
        queryClient.invalidateQueries({
          queryKey: getChatControllerGetRoomsQueryKey(),
        });
        console.log(
          "[useGlobalChatRealtime] Executed debounced chat list refetch",
        );
      }, 1000);
    };

    // Supplier notification channel
    const channelName =
      ChannelNamingUtil.getSupplierNotificationChannel(currentUserId);
    console.log(`[useGlobalChatRealtime] Subscribing to ${channelName}`);

    const channel = supabase
      .channel(channelName)
      .on("broadcast", { event: "new-message" }, (event) => {
        console.log("[useGlobalChatRealtime] New message received:", event);
        const message = event.payload;

        // ✅ Trigger debounced invalidation
        debouncedInvalidate();

        // 0. Filter out own messages
        if (
          message.senderId === currentUserId &&
          message.senderType === BaseChatMessageResponseDtoSenderType.supplier
        ) {
          return;
        }

        // 1. Prepare Toast Content
        const senderName = message.senderName || "고객";
        let preview = "새로운 메시지";
        
        switch (message.messageType) {
          case BaseChatMessageResponseDtoMessageType.text:
            preview = message.message;
            break;
          case BaseChatMessageResponseDtoMessageType.attachment:
            preview = "파일을 보냈습니다.";
            break;
          case BaseChatMessageResponseDtoMessageType.quote:
            preview = "견적서 답변이 도착했습니다.";
            break;
          case BaseChatMessageResponseDtoMessageType.contract:
            preview = "계약 요청이 도착했습니다.";
            break;
          case BaseChatMessageResponseDtoMessageType.payment_pending:
            preview = "결제 대기 중입니다.";
            break;
          case BaseChatMessageResponseDtoMessageType.system:
            preview = message.message;
            break;
          case BaseChatMessageResponseDtoMessageType.cancellation_request:
            preview = "주문 취소 요청이 도착했습니다.";
            break;
          default:
            preview = "새로운 메시지";
        }

        const avatarUrl = message.sender?.avatarUrl || message.sender?.avatar_url;

        // 2. Show Toast
        toast.custom(
          (t) => (
            <ChatNotificationToast
              senderName={senderName}
              message={preview}
              avatarUrl={avatarUrl}
              timestamp="지금"
              onClick={() => {
                toast.dismiss(t);
                // In embed mode, we might want to switch tabs or open a modal
                // For now, it just clears the toast.
              }}
            />
          ),
          {
            id: `chat-${message.roomId}`,
            duration: 5000,
          },
        );
      })
      .on("broadcast", { event: "message-read" }, (event) => {
        console.log("[useGlobalChatRealtime] Message read received:", event);

        // ✅ Invalidate Chat Room List
        queryClient.invalidateQueries({
          queryKey: getChatControllerGetRoomsQueryKey(),
        });
      })
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentUserId, queryClient]);
}
